import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Tree } from 'primereact/tree'
import { RadioButton } from 'primereact/radiobutton'
import { Color, LuxDialog, LuxIconSpinner } from '@lux/wardrobe-lib'
import { ResourceProps, ResourceType, TreeFolderProps } from '@/app/models/new-resource'
import './DialogSelectFolder.scss'

const DialogSelectFolder = (props: {
    title: string
    visible: boolean
    loading: boolean
    description: string
    folders: ResourceProps[]
    selectedFolderId: string
    resourceType: ResourceType
    onSave: () => void
    onHide: () => void
    setSelectedFolderId: (selectedFolderId: string) => void
}) => {
    const {
        title,
        folders,
        visible,
        loading,
        description,
        resourceType,
        selectedFolderId,
        onSave,
        onHide,
        setSelectedFolderId,
    } = props

    const intl = useIntl()

    const componentClass = 'smart-resources-dialog-select-folder'

    const [treeFolders, setTreeFolders] = useState<TreeFolderProps[]>([])

    useEffect(() => {
        if (folders) {
            let tmpTreeFolders = []
            let tmpFolders = [...folders]
            for (let i = 0; i < tmpFolders.length; i++) {
                let tmpFolder: ResourceProps = tmpFolders[i]
                let tmpTreeFolder: TreeFolderProps = {
                    ...tmpFolder,
                    children: [],
                    selected: false,
                    expanded: false,
                    expandable: resourceType !== ResourceType.subfolder,
                    selectable: resourceType === ResourceType.subfolder,
                    key: tmpFolder._id,
                    data: tmpFolder.title,
                    label: tmpFolder.title,
                    icon: 'cu cu-folder-plus',
                    folderType: tmpFolder.folderType,
                    resourceType: ResourceType.folder,
                }
                if (tmpFolder?.folders?.length > 0) {
                    let tmpTreeSubFolders = []
                    let tmpSubFolders = tmpFolder.folders
                    for (let x = 0; x < tmpSubFolders.length; x++) {
                        let tmpSubFolder: ResourceProps = tmpSubFolders[x]
                        let tmpTreeSubFolder: TreeFolderProps = {
                            ...tmpSubFolder,
                            children: [],
                            selected: false,
                            expanded: false,
                            selectable: true,
                            expandable: false,
                            icon: 'cu cu-folders',
                            key: tmpSubFolder._id,
                            data: tmpSubFolder.title,
                            label: tmpSubFolder.title,
                            folderType: tmpFolder.folderType,
                            resourceType: ResourceType.subfolder,
                        }
                        tmpTreeSubFolders.push(tmpTreeSubFolder)
                    }
                    tmpTreeFolder.children = tmpTreeSubFolders
                }
                if (resourceType === ResourceType.subfolder) {
                    tmpTreeFolders.push(tmpTreeFolder)
                } else {
                    if (tmpTreeFolder.children.length > 0) {
                        tmpTreeFolders.push(tmpTreeFolder)
                    }
                }
            }
            setTreeFolders(tmpTreeFolders)
        }
    }, [folders])

    const treeFolderTemplate = (treeFolder: TreeFolderProps, options: any) => {
        return treeFolder?.resourceType === ResourceType.folder ? (
            <span className={options.className}>{treeFolder.label}</span>
        ) : treeFolder?.resourceType === ResourceType.subfolder ? (
            <div
                onClick={() => onSelectSubFolder(treeFolder)}
                className={`${options.className} p-treenode-content-radiobutton${
                    treeFolder.selected ? ' selected' : ''
                }`}>
                <RadioButton
                    name={treeFolder.label}
                    value={treeFolder.label}
                    inputId={treeFolder.key}
                    checked={treeFolder.selected}
                />
                <label>{treeFolder.label}</label>
            </div>
        ) : (
            <></>
        )
    }

    const onClickTreeFolder = (treeFolder: TreeFolderProps) => {
        if (resourceType === ResourceType.subfolder) {
            if (!treeFolder.selectable) {
                return false
            }
            let newTreeFolders = []
            let tmpTreeFolders = [...treeFolders]
            for (let i = 0; i < tmpTreeFolders.length; i++) {
                let tmpTreeFolder = tmpTreeFolders[i]
                if (tmpTreeFolder.key === treeFolder.key) {
                    setSelectedFolderId(tmpTreeFolder.key)
                    newTreeFolders.push({ ...tmpTreeFolder, selected: true })
                } else {
                    newTreeFolders.push({ ...tmpTreeFolder, selected: false })
                }
            }
            setTreeFolders(newTreeFolders)
        } else {
            if (!treeFolder.expandable) {
                return false
            }
            let newTreeFolders = []
            let tmpTreeFolders = [...treeFolders]
            for (let i = 0; i < tmpTreeFolders.length; i++) {
                let tmpTreeFolder = tmpTreeFolders[i]
                if (tmpTreeFolder.key === treeFolder.key) {
                    newTreeFolders.push({ ...tmpTreeFolder, expanded: true })
                } else {
                    newTreeFolders.push({ ...tmpTreeFolder, expanded: false })
                }
            }
            setTreeFolders(newTreeFolders)
        }
    }

    const onSelectSubFolder = (treeFolder: TreeFolderProps) => {
        if (!treeFolder.selectable) {
            return false
        }
        let newTreeFolders = []
        let tmpTreeFolders = [...treeFolders]
        for (let i = 0; i < tmpTreeFolders.length; i++) {
            let tmpTreeFolder = tmpTreeFolders[i]
            if (tmpTreeFolder.key === treeFolder.key) {
                tmpTreeFolder = {
                    ...tmpTreeFolder,
                    selected: true,
                }
            } else {
                tmpTreeFolder = {
                    ...tmpTreeFolder,
                    selected: false,
                }
            }
            if (tmpTreeFolder?.children?.length > 0) {
                let newTreeFolderChildren = []
                let tmpTreeFolderChildren = tmpTreeFolder.children
                for (let x = 0; x < tmpTreeFolderChildren.length; x++) {
                    let tmpTreeFolderChild = tmpTreeFolderChildren[x]
                    if (tmpTreeFolderChild.key === treeFolder.key) {
                        setSelectedFolderId(tmpTreeFolderChild.key)
                        tmpTreeFolderChild = {
                            ...tmpTreeFolderChild,
                            selected: true,
                        }
                    } else {
                        tmpTreeFolderChild = {
                            ...tmpTreeFolderChild,
                            selected: false,
                        }
                    }
                    newTreeFolderChildren.push(tmpTreeFolderChild)
                }
                tmpTreeFolder.children = newTreeFolderChildren
            }
            newTreeFolders.push(tmpTreeFolder)
        }
        setTreeFolders(newTreeFolders)
    }

    const resetTreeFolders = () => {
        let newTreeFolders = []
        let tmpTreeFolders = [...treeFolders]
        for (let i = 0; i < tmpTreeFolders.length; i++) {
            let tmpTreeFolder = tmpTreeFolders[i]
            tmpTreeFolder = {
                ...tmpTreeFolder,
                expanded: false,
                selected: false,
            }
            if (tmpTreeFolder?.children?.length > 0) {
                let newTreeFolderChildren = []
                let tmpTreeFolderChildren = tmpTreeFolder.children
                for (let x = 0; x < tmpTreeFolderChildren.length; x++) {
                    let tmpTreeFolderChild = tmpTreeFolderChildren[x]
                    tmpTreeFolderChild = {
                        ...tmpTreeFolderChild,
                        selected: false,
                    }
                    newTreeFolderChildren.push(tmpTreeFolderChild)
                }
                tmpTreeFolder.children = newTreeFolderChildren
            }
            newTreeFolders.push(tmpTreeFolder)
        }
        setTreeFolders(newTreeFolders)
    }

    return (
        <div className={componentClass}>
            <LuxDialog
                onSave={onSave}
                onHide={() => {
                    onHide()
                    resetTreeFolders()
                }}
                title={title}
                visible={visible}
                customActions={true}
                description={description}
                maskClassName={`${componentClass}__mask`}
                cancelLabel={intl.formatMessage({
                    id: 'modals.dialog.buttons.back',
                    defaultMessage: 'Back',
                })}
                confirmLabel={intl.formatMessage({
                    id: 'modals.dialog.buttons.save',
                    defaultMessage: 'Save',
                })}
                actions={[
                    {
                        color: Color.BLACK,
                        variant: 'outlined',
                        onClick: () => {
                            onHide()
                            resetTreeFolders()
                        },
                        text: intl.formatMessage({
                            id: 'modals.dialog.buttons.back',
                            defaultMessage: 'Back',
                        }),
                    },
                    {
                        color: Color.WARNING,
                        onClick: () => onSave(),
                        disabled: selectedFolderId === '' || !selectedFolderId,
                        text: intl.formatMessage({
                            id: 'modals.dialog.buttons.save',
                            defaultMessage: 'Save',
                        }),
                    },
                ]}>
                <div className={`${componentClass}__tree`}>
                    {loading ? (
                        <div className={`${componentClass}__tree__loading`}>
                            <LuxIconSpinner />
                        </div>
                    ) : (
                        <Tree
                            value={treeFolders}
                            selectionMode="single"
                            selectionKeys={selectedFolderId}
                            nodeTemplate={treeFolderTemplate}
                            onNodeClick={(e: any) => onClickTreeFolder(e.node)}
                        />
                    )}
                </div>
            </LuxDialog>
        </div>
    )
}

export default DialogSelectFolder
